// Phase 3D.1-B4-B - Guarded frontend client for GET /api/demo/savoie.
//
// SAFETY CONTRACT:
//   * Activated ONLY by an explicit frontend flag REACT_APP_ENABLE_SAVOIE_DEMO === "true"
//     (case-insensitive, trimmed). Disabled by default.
//   * NO request is made when the flag is absent, false or invalid.
//   * NO request at import time (the fetch only runs inside useEffect at runtime).
//   * Strict contract validation (five explicit metadata fields + disclaimer + records[]).
//   * NO silent fallback: on any failure, data stays null and an explicit state is exposed.
//   * NO hardcoded Savoie figures / KPIs / dataset here (data comes only from the endpoint).
//
// Not wired to any page/route in B4-B.

import { useEffect, useState } from "react";

import api from "@/lib/api";
import { validateSavoieDemoContract } from "@/lib/savoieDemoContract";

export const SAVOIE_DEMO_FLAG = "REACT_APP_ENABLE_SAVOIE_DEMO";

// Explicit states surfaced by the hook.
export const SAVOIE_DEMO_STATES = Object.freeze({
  DISABLED: "disabled",
  LOADING: "loading",
  READY: "ready",
  UNAVAILABLE: "unavailable",
  INVALID_CONTRACT: "invalid_contract",
  ERROR: "error",
});

// Read the explicit flag at call time (never cached at module load -> no import side effect).
export function isSavoieDemoEnabled() {
  const raw = process.env.REACT_APP_ENABLE_SAVOIE_DEMO;
  return String(raw == null ? "" : raw).trim().toLowerCase() === "true";
}

export function useSavoieDemo() {
  const enabled = isSavoieDemoEnabled();
  const [state, setState] = useState(
    enabled ? SAVOIE_DEMO_STATES.LOADING : SAVOIE_DEMO_STATES.DISABLED
  );
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setState(SAVOIE_DEMO_STATES.DISABLED);
      return undefined;
    }
    let cancelled = false;
    setState(SAVOIE_DEMO_STATES.LOADING);
    // baseURL already includes "/api" -> real path is /api/demo/savoie.
    api
      .get("/demo/savoie")
      .then((res) => {
        if (cancelled) return;
        const { valid } = validateSavoieDemoContract(res && res.data);
        if (!valid) {
          setData(null); // no silent fallback
          setState(SAVOIE_DEMO_STATES.INVALID_CONTRACT);
          return;
        }
        setData(res.data);
        setState(SAVOIE_DEMO_STATES.READY);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null); // no silent fallback / no fabrication
        const status = err && err.response && err.response.status;
        setState(status === 404 ? SAVOIE_DEMO_STATES.UNAVAILABLE : SAVOIE_DEMO_STATES.ERROR);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { state, data, enabled };
}
